/**
 * Widget-versions selector-hook factory.
 *
 * Produces a `useWidgetVersions(agentId)` hook that reads the current widget version
 * history from a Zustand store passed in as `useStore`. The factory keeps the
 * entity layer free of any value-import from `@/app/store`, `@/features/`, or
 * `@/pages/`.
 *
 * **C4 dep-inversion rationale**: the previous implementation
 * (`features/widgets/hooks/useWidgetVersions/useWidgetVersions.ts`
 * pre-P5c.0b.1c) value-imported `useWidgetStore` from `@/app/store/widget`
 * (app layer) and two widget-data types from
 * `@/features/widgets/agents/<W>/...` (features layer). Both violated FSD
 * layer order. Per #1605, the store is now passed in as a hook reference,
 * and the widget-data shapes are inlined as structural anonymous types in
 * the version-title switch (consistent with the rest of the function which
 * already used inline `as { ... }` casts for several agent kinds).
 *
 * **Behavior preserved**: identical to the pre-P5c.0b.1c implementation —
 * same agent-label switch, same `getVersionTitle` switch, same
 * `getLatestVersionForAgent` reduction, same `useMemo` deps. Move-only +
 * dep-inversion. Bound by the `makeUseWidgetVersions(useStore)` factory at
 * the features-layer wiring file
 * `features/widgets/hooks/useWidgetVersions/index.ts`.
 */
import { useMemo } from 'react';
import { i18n } from '@/shared/i18n';
import type { VersionItem } from '@/shared/ui/VersionSelector';
import type { VersionConfig } from '@/shared/ui/SidePanelWrapper';
import {
  HtmlWidgetKey,
  isHtmlWidgetKey,
  type StrategicTerritoriesWidgetSchemaType,
} from '@inference.engine/widgets/web';

/**
 * Structural shape of a single widget version, matching the concrete
 * `WidgetVersion` type defined by the widget store. Captured here at the
 * entity layer so the factory can stay decoupled from `@/app/store`.
 */
export interface WidgetVersionView {
  id: string;
  version: number;
  messageId: string;
  agentId: string;
  widget: { data: unknown; sourceAgentId?: string; key?: string };
  timestamp: number;
  title?: string;
}

/**
 * Structural shape of the per-agent widget history entry, matching the
 * concrete `AgentWidgetHistory` type defined by the widget store.
 */
export interface AgentWidgetHistoryView {
  agentId: string;
  versions: WidgetVersionView[];
  activeVersionId?: string;
}

/**
 * Minimal slice of the widget store the factory reads via the `useStore`
 * hook reference passed at wire-up time.
 */
export interface WidgetVersionsStoreState {
  agentHistories: Record<string, AgentWidgetHistoryView>;
  switchToAgentVersion: (agentId: string, versionId: string) => void;
}

/**
 * Generic Zustand-style hook reference: callable with a selector that
 * projects the store state. The features-layer wiring file passes the
 * concrete `useWidgetStore` here; tests can inject a stub.
 */
export type UseWidgetVersionsStoreHook = <T>(selector: (state: WidgetVersionsStoreState) => T) => T;

/**
 * Get the display title for a widget version. Mirrors the pre-extraction
 * implementation, with the two former cross-feature type imports replaced
 * by inline structural casts (matching the style of the other branches).
 */
const viewAgentIdOf = (version: WidgetVersionView): string =>
  version.widget.sourceAgentId ?? version.agentId;

// A generated widget is stored under the id of whichever agent made it, so its versions are named
// by the widget itself rather than by that agent.
const isGeneratedWidget = ({ widget: { key = '' } }: WidgetVersionView) =>
  key === HtmlWidgetKey || isHtmlWidgetKey(key);

const getVersionTitle = (version: WidgetVersionView): string => {
  const data = version.widget.data;
  if (isGeneratedWidget(version)) {
    return (
      (data as { title?: string }).title?.trim() ||
      i18n.t('contentTest:widget.useWidgetVersions.widget')
    );
  }
  const viewAgentId = viewAgentIdOf(version);

  switch (viewAgentId) {
    case 'templates':
    case 'email-flow-agent': {
      // Prefer the email's subject line; fall back to the agent's label when it's blank. Append the
      // artifact's own version number as `#N` for a stable at-a-glance label. Note this is the
      // artifact version, NOT a globally-unique counter across the dropdown — two independent email
      // lineages could each show `#1`; the relative-time column is what visually disambiguates them.
      const subjectLine = (version.widget.data as { subjectLine?: string }).subjectLine?.trim();
      const base = subjectLine || getAgentLabel(viewAgentId);
      return `${base} #${version.version}`;
    }
    case 'briefing-agent': {
      const items = (version.widget.data as { items: Array<{ title: string }> }).items;
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      return items?.[0]?.title ?? version.title ?? '';
    }
    case 'social-media-brief': {
      const data = (
        version.widget.data as {
          items: Array<{
            brandName: string;
            overview: {
              opportunityTitle: string;
            };
          }>;
        }
      ).items[0];
      const title = data.overview.opportunityTitle;
      return title || data.brandName || i18n.t('contentTest:widget.useWidgetVersions.brief');
    }
    case 'strategy-agent-v2': {
      return i18n.t('contentTest:widget.useWidgetVersions.strategicTerritories');
    }
    case 'strategy-agent-v3': {
      const widgetData = data as StrategicTerritoriesWidgetSchemaType;
      return widgetData.title ?? '';
    }
    case 'copy-generation': {
      return (version.widget.data as { title: string }).title;
    }
    case 'image-generation': {
      const imageData = data as {
        title?: string;
        images?: Array<{ metadata?: { fileName?: string } }>;
      };
      return (
        imageData.title ||
        imageData.images?.[0]?.metadata?.fileName ||
        version.title ||
        i18n.t('contentTest:widget.useWidgetVersions.generatedImage')
      );
    }
    case 'video-generation': {
      const videoData = data as {
        title?: string;
        video?: { metadata?: { fileName?: string } };
      };
      return (
        videoData.title ||
        videoData.video?.metadata?.fileName ||
        version.title ||
        i18n.t('contentTest:widget.useWidgetVersions.generatedVideo')
      );
    }
    case 'ideation':
      return `${version.title} - v${version.version}`;
    default:
      return (
        version.title ||
        i18n.t('contentTest:widget.useWidgetVersions.version', { version: version.version })
      );
  }
};

const toTitleCase = (value: string) =>
  value.replace(/[-_]/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());

/**
 * Map agent IDs to a human readable label for the version selector.
 */
const getAgentLabel = (agentId: string): string => {
  switch (agentId) {
    case 'email-flow-agent':
      return i18n.t('contentTest:widget.useWidgetVersions.email');
    case 'briefing-agent':
      return i18n.t('contentTest:widget.useWidgetVersions.briefing');
    case 'social-media-brief':
      return i18n.t('contentTest:widget.useWidgetVersions.brief');
    case 'strategy-agent-v2':
    case 'strategy-agent-v3':
      return i18n.t('contentTest:widget.useWidgetVersions.strategy');
    case 'copy-generation':
      return i18n.t('contentTest:widget.useWidgetVersions.copy');
    case 'image-generation':
      return i18n.t('contentTest:widget.useWidgetVersions.image');
    case 'video-generation':
      return i18n.t('contentTest:widget.useWidgetVersions.video');
    case 'insight-agent':
      return i18n.t('contentTest:widget.useWidgetVersions.insight');
    default:
      return toTitleCase(agentId);
  }
};

const getLatestVersionForAgent = (
  history: AgentWidgetHistoryView | undefined,
  versionMap: Map<string, VersionItem>,
): VersionItem | undefined => {
  if (!history || history.versions.length === 0) {
    return undefined;
  }

  const latest = history.versions.reduce<WidgetVersionView | undefined>((acc, current) => {
    if (!acc) return current;
    return current.timestamp > acc.timestamp ? current : acc;
  }, undefined);

  return latest ? versionMap.get(latest.id) : undefined;
};

/**
 * Build a `useWidgetVersions(agentId)` hook bound to the supplied widget
 * store hook. The bound hook returns a `VersionConfig` to pass to
 * `SidePanelHeader`, or `undefined` if no versions are available.
 */
export const makeUseWidgetVersions = (useStore: UseWidgetVersionsStoreHook) => {
  return (agentId: string): VersionConfig | undefined => {
    const agentHistories = useStore((state) => state.agentHistories);
    const switchToAgentVersion = useStore((state) => state.switchToAgentVersion);

    return useMemo(() => {
      const histories = Object.values(agentHistories);
      const versions: VersionItem[] = histories.flatMap((history) =>
        history.versions
          .filter((v) => v.agentId === agentId)
          .map((v) => ({
            id: v.id,
            title: getVersionTitle(v),
            timestamp: v.timestamp,
            version: v.version,
            agentId: v.agentId,
            agentLabel: isGeneratedWidget(v)
              ? i18n.t('contentTest:widget.useWidgetVersions.widget')
              : getAgentLabel(viewAgentIdOf(v)),
          })),
      );

      if (versions.length <= 1) {
        return undefined;
      }

      const versionMap = new Map<string, VersionItem>();
      versions.forEach((v) => versionMap.set(v.id, v));

      const currentHistory = agentHistories[agentId];
      const activeVersionId = currentHistory.activeVersionId;
      const activeVersion = activeVersionId ? versionMap.get(activeVersionId) : undefined;

      const latestForAgent = getLatestVersionForAgent(currentHistory, versionMap);
      const latestOverall = versions.reduce<VersionItem | undefined>((acc, current) => {
        if (!acc) return current;
        return current.timestamp > acc.timestamp ? current : acc;
      }, undefined);

      const currentVersion = activeVersion ?? latestForAgent ?? latestOverall;

      if (!currentVersion) {
        return undefined;
      }

      return {
        currentVersion,
        versions,
        onVersionSelect: (version: VersionItem) => {
          const target = versionMap.get(version.id);
          if (!target?.agentId) return;
          switchToAgentVersion(target.agentId, target.id);
        },
      };
    }, [agentHistories, agentId, switchToAgentVersion]);
  };
};
